import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, ArrowUpDown, Download, X } from 'lucide-react';
import { getAllReferralIds } from '@/utils/referral/bank';
import { ReferralId } from '@/utils/referral/bank/types';

interface ReferralIdExtended {
  id: string;
  code: string;
  instanceId?: string;
  userId?: string;
  isAssigned?: boolean;
  createdAt: Date;
  updatedAt?: Date;
  adminDomain?: string;
  referral?: string;
  referredBy?: string;
  status?: 'active' | 'pending' | 'expired' | 'unused' | 'used';
}

const enhanceReferralData = (referralIds: ReferralId[]): ReferralIdExtended[] => {
  return referralIds.map(id => {
    const mockStatuses = ['active', 'pending', 'expired', 'unused', 'used'];
    const mockDomains = id.instanceId ? 
      [`admin.instance-${id.instanceId.substring(0, 5)}.com`] : 
      ['unassigned'];
    
    return {
      ...id,
      code: id.code || `REF-${id.id.substring(0, 6)}`,
      adminDomain: id.instanceId ? mockDomains[0] : undefined,
      referral: id.isAssigned ? `user-${Math.floor(Math.random() * 1000)}@example.com` : undefined,
      referredBy: id.isAssigned ? `referrer-${Math.floor(Math.random() * 1000)}@example.com` : undefined,
      status: (id.status as 'active' | 'pending' | 'expired' | 'unused' | 'used') || 
        (id.isAssigned ? mockStatuses[Math.floor(Math.random() * 3)] as 'active' | 'pending' | 'expired' : 'unused'),
    };
  });
};

interface ReferralIdReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ReferralIdReportModal: React.FC<ReferralIdReportModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<keyof ReferralIdExtended>('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  const allReferralIds = useMemo(() => {
    const ids = getAllReferralIds();
    return enhanceReferralData(ids);
  }, []);
  
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...allReferralIds];
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(id => id.status === statusFilter);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(id => 
        id.code.toLowerCase().includes(query) ||
        (id.instanceId?.toLowerCase().includes(query)) ||
        (id.adminDomain?.toLowerCase().includes(query)) ||
        (id.referral?.toLowerCase().includes(query)) ||
        (id.referredBy?.toLowerCase().includes(query)) ||
        (id.status?.toLowerCase().includes(query))
      );
    }
    
    return filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return sortDirection === 'asc' ? 1 : -1;
      if (bValue === undefined) return sortDirection === 'asc' ? -1 : 1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortDirection === 'asc' 
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }
      
      return 0;
    });
  }, [allReferralIds, searchQuery, sortField, sortDirection, statusFilter]);
  
  const handleSort = (field: keyof ReferralIdExtended) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const handleExportCSV = () => {
    const headers = ['Referral Code', 'Instance ID', 'Admin Domain', 'Referral', 'Referred By', 'Status', 'Created Date'];
    
    const csvData = filteredAndSortedData.map(id => [
      id.code,
      id.instanceId || 'Unassigned',
      id.adminDomain || 'Unassigned',
      id.referral || 'N/A',
      id.referredBy || 'N/A',
      id.status || 'N/A',
      new Date(id.createdAt).toLocaleDateString()
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `referral-id-report-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Referral ID Assignment Report</DialogTitle>
          <DialogDescription>
            Detailed report of all referral IDs and their assignments
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="search"
                placeholder="Search by any field..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="unused">Unused</SelectItem>
                  <SelectItem value="used">Used</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" onClick={handleExportCSV} className="flex items-center">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
          
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('code')}>
                    <div className="flex items-center">
                      Referral Code
                      {sortField === 'code' && (
                        <ArrowUpDown className={`ml-2 h-4 w-4 ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('instanceId')}>
                    <div className="flex items-center">
                      Instance ID
                      {sortField === 'instanceId' && (
                        <ArrowUpDown className={`ml-2 h-4 w-4 ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('adminDomain')}>
                    <div className="flex items-center">
                      Admin Domain
                      {sortField === 'adminDomain' && (
                        <ArrowUpDown className={`ml-2 h-4 w-4 ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('referral')}>
                    <div className="flex items-center">
                      Referral
                      {sortField === 'referral' && (
                        <ArrowUpDown className={`ml-2 h-4 w-4 ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('referredBy')}>
                    <div className="flex items-center">
                      Referred By
                      {sortField === 'referredBy' && (
                        <ArrowUpDown className={`ml-2 h-4 w-4 ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('status')}>
                    <div className="flex items-center">
                      Status
                      {sortField === 'status' && (
                        <ArrowUpDown className={`ml-2 h-4 w-4 ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('createdAt')}>
                    <div className="flex items-center">
                      Date Created
                      {sortField === 'createdAt' && (
                        <ArrowUpDown className={`ml-2 h-4 w-4 ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                      )}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedData.length > 0 ? (
                  filteredAndSortedData.map((id) => (
                    <TableRow key={id.id}>
                      <TableCell className="font-mono">{id.code}</TableCell>
                      <TableCell>{id.instanceId || 'Unassigned'}</TableCell>
                      <TableCell>{id.adminDomain || 'Unassigned'}</TableCell>
                      <TableCell>{id.referral || 'N/A'}</TableCell>
                      <TableCell>{id.referredBy || 'N/A'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          id.status === 'active' ? 'bg-green-100 text-green-800' :
                          id.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          id.status === 'expired' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {id.status || 'Unknown'}
                        </span>
                      </TableCell>
                      <TableCell>{new Date(id.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      No referral IDs found matching your criteria
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReferralIdReportModal;
